import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { BaseComponent } from '@isophi/core-ui';
import { OrganizationWorkplaceDetail } from '@isophi/mng-api';
import { addMonths, isWithinInterval } from 'date-fns';
import { BehaviorSubject, combineLatest, filter, map, shareReplay, take, takeUntil } from 'rxjs';

import { RouterLinkFactory } from '../../../../../core/router/router-link.factory';
import { UserService } from '../../../../../core/services/user.service';

type ExtendedOrganizationWorkplaceLicense = OrganizationWorkplaceDetail['licenses'][number] & {
  aboutToExpire: boolean;
  expired: boolean;
  pedagogIcon: boolean;
};

@Component({
  selector: 'app-organization-workplace-item',
  templateUrl: './organization-workplace-item.component.html',
  styleUrls: ['./organization-workplace-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OrganizationWorkplaceItemComponent extends BaseComponent {
  protected readonly links = inject(RouterLinkFactory);
  protected readonly userService = inject(UserService);
  @Input() set workplace(value: OrganizationWorkplaceDetail) {
    this.workplace$.next(value);
  }

  protected readonly workplace$ = new BehaviorSubject<OrganizationWorkplaceDetail | null>(null);
  protected readonly expanded$ = new BehaviorSubject(false);

  protected readonly workplaceLicenses$ = this.workplace$.pipe(
    map((workplace) => {
      if (!workplace || !workplace.licenses) {
        return [];
      }

      return workplace.licenses.map((license) => {
        const currentDate = new Date();
        let aboutToExpire = false;
        if (
          license.validTo &&
          isWithinInterval(license.validTo, {
            start: currentDate,
            end: addMonths(currentDate, 1),
          })
        ) {
          aboutToExpire = true;
        }
        let expired = false;
        if (license.validTo && new Date(license.validTo) < currentDate) {
          expired = true;
          aboutToExpire = false;
        }
        return {
          ...license,
          aboutToExpire: aboutToExpire,
          expired: expired,
          pedagogIcon: license.licenseTitle.toLowerCase().includes('podpor'),
        };
      }) as ExtendedOrganizationWorkplaceLicense[];
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  protected readonly currentSelectedUuid$ = new BehaviorSubject(this.userService.getCurrentKindergartenUUID());

  constructor() {
    super();

    // expand workplace if its the same as currently used
    combineLatest([this.workplace$, this.currentSelectedUuid$])
      .pipe(
        filter(([workplace, selectedUuid]) => !!workplace && !!selectedUuid),
        take(1),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([workplace, selectedUuid]) => {
        if (workplace.uuid === selectedUuid) {
          this.expanded$.next(true);
        }
      });
  }
}
